// =============================================================================
// sales-campaigns.ts — Contrato Zod compartilhado do ciclo de vida de
// campanhas comerciais (`sales_campaigns`, F35-S01/F35-S02).
//
// Fonte única consumida pela API (validação de request/response,
// modules/sales/campaigns) e pelo frontend (F35-S07+) — nenhum dos dois
// lados redeclara enums ou o shape de resposta.
//
// Convenção de payload (igual a sales-appointments.ts): camelCase em toda a
// borda, paginação via limit/offset, datas como string ISO 8601 (UTC, com
// offset) ou null.
//
// FORA DE ESCOPO deste contrato (F35-S02): `audienceFilters`/
// `audienceVersion` são somente LEITURA aqui — a escrita é versionada
// (INSERT-only em `campaign_audience_versions`) e ganha rota própria em
// F35-S03 (ver header de apps/api/src/db/schema/salesCampaigns.ts). Criar ou
// editar uma campanha por este contrato NUNCA altera o filtro de público.
//
// LGPD (doc 17 §8.3/§8.5): `name`/`description` são metadados definidos pelo
// gestor (não são dados do titular). `audienceFilters` guarda apenas
// ids/faixas — nunca texto livre com PII (ver salesCampaigns.ts).
// =============================================================================
import { z } from 'zod';
import { LeadStatusSchema } from './leads.js';
import { AssignmentStrategySchema, CampaignMemberStatusSchema, CampaignStatusSchema, SalesOutcomeCodeSchema, } from './sales.js';
// ---------------------------------------------------------------------------
// Helper de querystring — scalar→array (mesmo padrão de sales-appointments.ts).
// ---------------------------------------------------------------------------
function arrayQueryParam(schema) {
    return z.preprocess((v) => (v === undefined || v === null ? undefined : Array.isArray(v) ? v : [v]), z.array(schema).optional());
}
// ---------------------------------------------------------------------------
// weekly_window — janela semanal de contato permitido.
// ---------------------------------------------------------------------------
/** Chaves de dia da semana aceitas em `weeklyWindow` (abreviação de 3 letras, inglês minúsculo). */
export const CAMPAIGN_WINDOW_DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const TIME_HHMM_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;
const CampaignDayWindowSchema = z
    .object({
    start: z.string().regex(TIME_HHMM_REGEX, 'Horário inválido (esperado HH:MM)'),
    end: z.string().regex(TIME_HHMM_REGEX, 'Horário inválido (esperado HH:MM)'),
})
    .refine((w) => w.start < w.end, {
    message: '"start" deve ser anterior a "end"',
    path: ['end'],
});
/**
 * Janela semanal de contato permitido, por dia. Dia ausente = sem janela
 * configurada para aquele dia (nunca "24h liberado") — ver
 * `salesCampaigns.ts` para a semântica completa.
 */
export const CampaignWeeklyWindowSchema = z.record(z.enum(CAMPAIGN_WINDOW_DAY_KEYS), CampaignDayWindowSchema);
// ---------------------------------------------------------------------------
// Response — uma campanha comercial
// ---------------------------------------------------------------------------
export const CampaignSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    cityId: z.string().uuid(),
    /** `users.id` do gestor responsável pela campanha. */
    ownerUserId: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    /** Produto de crédito associado, quando aplicável. */
    productId: z.string().uuid().nullable(),
    /** Maior = mais prioritário. Desempata entre campanhas concorrentes na fila compartilhada. */
    priority: z.number().int(),
    status: CampaignStatusSchema,
    assignmentStrategy: AssignmentStrategySchema,
    weeklyWindow: CampaignWeeklyWindowSchema,
    /** Fuso IANA usado para interpretar `weeklyWindow` (ex.: "America/Porto_Velho"). */
    timezone: z.string(),
    maxAttempts: z.number().int().positive(),
    minIntervalMinutes: z.number().int().min(0),
    /**
     * Snapshot ATUAL do filtro de público — somente LEITURA neste contrato
     * (ver aviso de escopo no cabeçalho do arquivo). `{}` = nenhum filtro
     * publicado ainda.
     */
    audienceFilters: z.record(z.string(), z.unknown()),
    audienceVersion: z.number().int().positive(),
    /** Preenchido na 1ª transição para `active`. Nunca sobrescrito por um `resume` subsequente. */
    activatedAt: z.string().nullable(),
    /** Timestamp da última pausa (`active` → `paused`). */
    pausedAt: z.string().nullable(),
    completedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
// ---------------------------------------------------------------------------
// List query — GET /api/sales/campaigns
// ---------------------------------------------------------------------------
export const CampaignListQuerySchema = z.object({
    status: arrayQueryParam(CampaignStatusSchema),
    /** Filtro por proprietário (`users.id`). Ator sem `manage` sempre fica restrito ao próprio. */
    ownerUserId: arrayQueryParam(z.string().uuid()),
    productId: arrayQueryParam(z.string().uuid()),
    /** Janela sobre `createdAt` — "período" de criação da campanha (doc 26 §9). */
    createdFrom: z.string().datetime({ offset: true }).optional(),
    createdTo: z.string().datetime({ offset: true }).optional(),
    sortBy: z.enum(['createdAt', 'name', 'priority']).default('createdAt'),
    sortOrder: z.enum(['asc', 'desc']).default('desc'),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
// ---------------------------------------------------------------------------
// List response
// ---------------------------------------------------------------------------
export const CampaignListResponseSchema = z.object({
    data: z.array(CampaignSchema),
    total: z.number().int().describe('Total de campanhas que atendem ao filtro'),
    limit: z.number().int(),
    offset: z.number().int(),
});
// ---------------------------------------------------------------------------
// Create — POST /api/sales/campaigns
//
// Nasce sempre em `status='draft'` — salvar NUNCA ativa (doc 26 §6.6/§9).
// `audienceFilters` não é aceito aqui (ver aviso de escopo no cabeçalho).
// ---------------------------------------------------------------------------
export const CampaignCreateBodySchema = z.object({
    cityId: z.string().uuid(),
    /** Omitir = o próprio ator autenticado vira o proprietário. */
    ownerUserId: z.string().uuid().optional(),
    name: z.string().min(1).max(200),
    description: z.string().min(1).max(2000).optional(),
    productId: z.string().uuid().optional(),
    /** Omitir usa o default conservador 0 (prioridade normal). */
    priority: z.number().int().min(0).max(1000).optional(),
    /** Omitir usa o default `shared_queue`. */
    assignmentStrategy: AssignmentStrategySchema.optional(),
    /** Omitir usa `{}` (nenhuma janela configurada — campanha ainda não pronta para operar). */
    weeklyWindow: CampaignWeeklyWindowSchema.optional(),
    /** Omitir usa o default `America/Porto_Velho`. */
    timezone: z.string().min(1).max(64).optional(),
    /** Omitir usa o default 5. */
    maxAttempts: z.number().int().positive().max(50).optional(),
    /** Omitir usa o default 60. */
    minIntervalMinutes: z.number().int().min(0).max(10080).optional(),
});
// ---------------------------------------------------------------------------
// Patch — PATCH /api/sales/campaigns/:id
//
// Edição permitida POR ESTADO (verificado no service, não aqui — este schema
// só valida o shape). Em `draft`/`ready` todos os campos abaixo são
// editáveis; em `active`/`paused` apenas os campos operacionais
// (`priority`, `weeklyWindow`, `timezone`, `maxAttempts`,
// `minIntervalMinutes`) — ver modules/sales/campaigns/service.ts. `cityId`
// nunca é editável (mesma convenção de `assignedAgentId` em
// contact_appointments: reatribuição é uma ação distinta, fora de escopo).
// `audienceFilters` não é editável aqui (ver aviso de escopo no cabeçalho).
// ---------------------------------------------------------------------------
export const CampaignPatchBodySchema = z
    .object({
    name: z.string().min(1).max(200).optional(),
    /** null limpa o campo; omitir mantém o valor atual. */
    description: z.string().min(1).max(2000).nullable().optional(),
    /** null limpa o campo; omitir mantém o valor atual. */
    productId: z.string().uuid().nullable().optional(),
    /** Transferência de titularidade — exige `sales_campaigns:manage` (mesmo piso do PATCH inteiro). */
    ownerUserId: z.string().uuid().optional(),
    priority: z.number().int().min(0).max(1000).optional(),
    assignmentStrategy: AssignmentStrategySchema.optional(),
    weeklyWindow: CampaignWeeklyWindowSchema.optional(),
    timezone: z.string().min(1).max(64).optional(),
    maxAttempts: z.number().int().positive().max(50).optional(),
    minIntervalMinutes: z.number().int().min(0).max(10080).optional(),
})
    .refine((body) => Object.keys(body).length > 0, {
    message: 'Informe ao menos um campo para atualizar',
});
// ---------------------------------------------------------------------------
// Fila de campanha — claim/release/heartbeat (F35-S05, doc 26 §8/§9).
//
// Convenção de payload igual ao resto do arquivo: camelCase, datas ISO 8601.
// `member` reflete `campaign_members` (sem PII — LGPD §8.5); `lead` é o
// ÚNICO ponto deste contrato que carrega PII do titular (nome/telefone) —
// necessário para o vendedor trabalhar o contato reservado. Nunca reusar
// `CampaignQueueLeadContextSchema` em evento/log/outbox.
// ---------------------------------------------------------------------------
/** Um membro de fila (`campaign_members`), na borda HTTP. */
export const CampaignQueueMemberSchema = z.object({
    id: z.string().uuid(),
    campaignId: z.string().uuid(),
    leadId: z.string().uuid(),
    status: CampaignMemberStatusSchema,
    priority: z.number().int(),
    attemptCount: z.number().int(),
    lastOutcomeCode: SalesOutcomeCodeSchema.nullable(),
    /**
     * Próximo momento em que o membro volta a ficar elegível — só preenchido
     * quando `status='waiting_callback'` (F35-S15, hora combinada com o
     * titular). `null` nos demais status.
     */
    nextActionAt: z.string().nullable(),
    claimedByUserId: z.string().uuid().nullable(),
    /** Prazo da reserva atual — null quando `status` não é `claimed`. */
    claimExpiresAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** Contexto mínimo do lead reservado — o necessário para o vendedor ligar/conversar. */
export const CampaignQueueLeadContextSchema = z.object({
    id: z.string().uuid(),
    name: z
        .string()
        .describe('Nome do titular do lead — PII, exibido apenas ao vendedor dono do lease'),
    phone: z.string().describe('Telefone E.164 do titular — PII'),
    cityId: z.string().uuid().nullable(),
});
/** Resposta de `POST /queue/claim-next` quando há um membro disponível (200). */
export const CampaignQueueClaimResponseSchema = z.object({
    member: CampaignQueueMemberSchema,
    lead: CampaignQueueLeadContextSchema,
});
/** Body de `POST /queue/:memberId/release` — motivo opcional, livre. */
export const CampaignQueueReleaseBodySchema = z.object({
    reason: z.string().min(1).max(500).optional(),
});
/** Resposta de `POST /queue/:memberId/release`. */
export const CampaignQueueReleaseResponseSchema = z.object({
    member: CampaignQueueMemberSchema,
});
/** Resposta de `POST /queue/:memberId/heartbeat` — só o essencial para o front renovar o timer local. */
export const CampaignQueueHeartbeatResponseSchema = z.object({
    memberId: z.string().uuid(),
    claimExpiresAt: z.string(),
});
/**
 * Body de `POST /queue/:memberId/resolve` — registra o desfecho de um
 * membro reservado e encerra o lease (F35-S15).
 *
 * `nextActionAt` é obrigatório quando `outcomeCode` exige um retorno
 * agendado (hoje só `callback_requested` — `SALES_OUTCOME_META[outcomeCode]
 * .requiresAppointment`, nunca uma lista escrita à mão no service). Deve ser
 * um instante FUTURO — a fila não pode ficar elegível antes da hora que o
 * titular pediu (doc 26 §12).
 */
export const CampaignQueueResolveBodySchema = z.object({
    outcomeCode: SalesOutcomeCodeSchema.describe('Desfecho comercial registrado para este membro (mesmo vocabulário de contact_attempts.outcome_code).'),
    nextActionAt: z
        .string()
        .datetime({ offset: true })
        .optional()
        .describe('Hora combinada do retorno — obrigatório quando o desfecho exige agendamento (ex.: callback_requested).'),
    reason: z.string().min(1).max(500).optional(),
});
/** Resposta de `POST /queue/:memberId/resolve`. */
export const CampaignQueueResolveResponseSchema = z.object({
    member: CampaignQueueMemberSchema,
});
// ---------------------------------------------------------------------------
// Público da campanha — prévia/apply (F35-S03, doc 26 §4.2/§6.6 etapa 3).
//
// Contrato de ENTRADA (`AudienceSourceSchema`) compartilhado por
// `POST /:id/audience/preview` (não escreve) e `POST /:id/audience/apply`
// (materializa) — a mesma função de classificação do backend (`classifyAudience`,
// modules/sales/campaigns/audience/service.ts) consome exatamente este shape
// nos dois endpoints, para que prévia e aplicação NUNCA divirjam (DoD do
// slot: "prévia diz 1.200, campanha nasce com 900" é o bug que este contrato
// único existe para prevenir).
//
// `type: 'filter'` — filtro do CRM (ids/faixas, nunca texto livre — LGPD,
// ver aviso em salesCampaigns.ts). `type: 'manual'` — seleção explícita de
// leads. `type: 'import'` (referência a um lote de importação) fica para
// F35-S04, que é dono do parsing de arquivo — este contrato não o declara
// ainda para não travar um `type` que a API não sabe resolver.
// ---------------------------------------------------------------------------
/**
 * Filtro de público — somente ids/faixas (nunca termo de busca livre,
 * LGPD doc 17 §8.3: nome/CPF digitado não pode ser persistido em
 * `audience_filters`). `.strict()` rejeita qualquer campo fora desta lista,
 * inclusive um eventual campo de busca textual adicionado por engano no
 * front.
 */
export const AudienceFilterSchema = z
    .object({
    /** Status do lead no pipeline CRM (`leads.status`). */
    leadStatus: z.array(LeadStatusSchema).min(1).optional(),
    /** `agents.id` do agente responsável pelo lead (`leads.agent_id`). */
    assignedAgentIds: z.array(z.string().uuid()).min(1).optional(),
    /** Janela sobre `leads.created_at` — início. */
    createdFrom: z.string().datetime({ offset: true }).optional(),
    /** Janela sobre `leads.created_at` — fim. */
    createdTo: z.string().datetime({ offset: true }).optional(),
    /** Ids a excluir explicitamente do resultado do filtro. */
    excludeLeadIds: z.array(z.string().uuid()).optional(),
})
    .strict();
/**
 * Fonte do público solicitado — discriminada por `type`. Mesmo shape
 * consumido pela prévia e pela aplicação (ver aviso acima). Persistido
 * verbatim (após canonicalização determinística) em
 * `campaign_audience_versions.filters` / `sales_campaigns.audience_filters`
 * quando a aplicação publica uma nova versão.
 */
export const AudienceSourceSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('filter'), filter: AudienceFilterSchema }),
    z.object({
        type: z.literal('manual'),
        leadIds: z.array(z.string().uuid()).min(1).max(5000),
    }),
]);
/** Body de `POST /:id/audience/preview` — idêntico ao de `/apply` (ver aviso acima). */
export const AudiencePreviewBodySchema = AudienceSourceSchema;
/** Body de `POST /:id/audience/apply`. */
export const AudienceApplyBodySchema = AudienceSourceSchema;
/**
 * Categorias de higienização (doc 26 §4.2: "Higienizar duplicados, registros
 * bloqueados, dados inválidos e clientes já presentes"). `new`/`reused`
 * SOMAM `willMaterialize`/`materializedCount` — as demais são excluídas da
 * materialização.
 */
export const AudienceCountsSchema = z.object({
    /** Nunca trabalhado em nenhuma campanha — entra como novo membro. */
    new: z.number().int().min(0),
    /** Já é membro ativo (não removido) de OUTRA campanha `active`/`paused` — ainda assim entra nesta. */
    reused: z.number().int().min(0),
    /** Mesmo lead id repetido dentro do próprio pedido (filtro/seleção) — só a 1ª ocorrência conta. */
    duplicate: z.number().int().min(0),
    /** Não encontrado no escopo (org/cidade/ator) ou sem telefone utilizável. */
    invalid: z.number().int().min(0),
    /** `assertContactAllowed` bloqueou algum canal de voz — nunca materializado como trabalhável. */
    blocked: z.number().int().min(0),
    /** Já é membro (qualquer status) DESTA campanha — `UNIQUE(campaign_id, lead_id)`. */
    alreadyPresent: z.number().int().min(0),
});
/** Resposta de `POST /:id/audience/preview` — NUNCA escreve no banco (DoD do slot). */
export const AudiencePreviewResponseSchema = z.object({
    campaignId: z.string().uuid(),
    /** Total de ids resolvidos da fonte, ANTES da deduplicação interna. */
    requested: z.number().int().min(0),
    counts: AudienceCountsSchema,
    /** `counts.new + counts.reused` — quantos membros `/apply` efetivamente criaria agora. */
    willMaterialize: z.number().int().min(0),
});
/** Resposta de `POST /:id/audience/apply`. */
export const AudienceApplyResponseSchema = z.object({
    campaignId: z.string().uuid(),
    /** Versão ATUAL do filtro após esta chamada (só incrementa se o filtro publicado mudou). */
    audienceVersion: z.number().int().positive(),
    /** `true` quando esta chamada publicou uma NOVA versão em `campaign_audience_versions`. */
    versionChanged: z.boolean(),
    counts: AudienceCountsSchema,
    /** Membros efetivamente inseridos NESTA chamada (novo + reutilizado + bloqueado). Idempotente: reaplicar não reinsere quem já é membro. */
    materializedCount: z.number().int().min(0),
});
// ---------------------------------------------------------------------------
// Carteira/membros de campanha (F35-S04, doc 26 §4.2/§9/§11).
//
// `entry_source`/`exclusion_reason` — promovidos aqui a partir dos CHECKs
// locais de `db/schema/campaignMembers.ts` (F35-S01): aquele arquivo
// documentava explicitamente que a promoção para o pacote compartilhado
// ficaria a cargo do primeiro slot que precisasse validar os mesmos valores
// na borda HTTP fora do módulo de fila (F35-S05) — este é esse slot. Os
// valores abaixo têm que continuar EXATAMENTE iguais aos dois `as const`
// locais de campaignMembers.ts (a tabela documenta a semântica de cada
// valor; não duplicamos os comentários aqui para não divergir do texto
// canônico).
// ---------------------------------------------------------------------------
export const CAMPAIGN_MEMBER_ENTRY_SOURCES = ['filter', 'import', 'manual'];
export const CampaignMemberEntrySourceSchema = z.enum(CAMPAIGN_MEMBER_ENTRY_SOURCES);
export const CAMPAIGN_MEMBER_EXCLUSION_REASONS = [
    'duplicate',
    'contact_blocked',
    'invalid_data',
    'already_present',
    'manual_removal',
];
export const CampaignMemberExclusionReasonSchema = z.enum(CAMPAIGN_MEMBER_EXCLUSION_REASONS);
/** Um membro da carteira (`campaign_members`), na borda HTTP — sem PII (LGPD §8.5, ver campaignMembers.ts). */
export const CampaignMemberSchema = z.object({
    id: z.string().uuid(),
    campaignId: z.string().uuid(),
    leadId: z.string().uuid(),
    assignedAgentId: z.string().uuid().nullable(),
    status: CampaignMemberStatusSchema,
    priority: z.number().int(),
    /** Próximo momento em que o membro volta a ficar disponível. `null` = disponível assim que `status='available'`. */
    nextActionAt: z.string().nullable(),
    attemptCount: z.number().int(),
    lastOutcomeCode: SalesOutcomeCodeSchema.nullable(),
    claimedByUserId: z.string().uuid().nullable(),
    claimExpiresAt: z.string().nullable(),
    entrySource: CampaignMemberEntrySourceSchema,
    exclusionReason: CampaignMemberExclusionReasonSchema.nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
// ---------------------------------------------------------------------------
// GET /:id/members — listagem paginada da carteira.
// ---------------------------------------------------------------------------
export const CampaignMembersListQuerySchema = z.object({
    status: arrayQueryParam(CampaignMemberStatusSchema),
    /** `agents.id` do responsável pelo membro (`campaign_members.assigned_agent_id`). */
    assignedAgentId: arrayQueryParam(z.string().uuid()),
    /** Janela sobre `campaign_members.next_action_at` — início. */
    nextActionAfter: z.string().datetime({ offset: true }).optional(),
    /** Janela sobre `campaign_members.next_action_at` — fim. */
    nextActionBefore: z.string().datetime({ offset: true }).optional(),
    sortBy: z.enum(['createdAt', 'priority', 'nextActionAt']).default('priority'),
    sortOrder: z.enum(['asc', 'desc']).default('desc'),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
export const CampaignMembersListResponseSchema = z.object({
    data: z.array(CampaignMemberSchema),
    total: z.number().int().describe('Total de membros que atendem ao filtro'),
    limit: z.number().int(),
    offset: z.number().int(),
});
// ---------------------------------------------------------------------------
// POST /:id/members/import — materializa a carteira a partir de um lote de
// importação JÁ CONCLUÍDO (`import_batches.status='completed'`,
// `entity_type='leads'`). Reusa o pipeline de importação como está (doc 26
// §9: "sem passar por planilha intermediária") — este endpoint não recebe
// arquivo, apenas a referência de um lote que o módulo de importações
// (F1-S17) já processou e persistiu.
// ---------------------------------------------------------------------------
export const CampaignMembersImportBodySchema = z.object({
    importBatchId: z
        .string()
        .uuid()
        .describe('import_batches.id de um lote já concluído (status="completed", entityType="leads") — os leads persistidos desse lote entram na campanha.'),
});
/**
 * Resultado do lote de importação aplicado à campanha (doc 26 §11): além dos
 * 4 baldes citados no DoD (`added`/`reused`/`blocked`/`rejected`), inclui
 * `alreadyPresent` (informativo — não re-inserido) para o total bater com
 * `requested`.
 */
export const CampaignMembersImportResponseSchema = z.object({
    campaignId: z.string().uuid(),
    importBatchId: z.string().uuid(),
    /** Total de leads persistidos no lote considerados candidatos. */
    requested: z.number().int().min(0),
    /** Novos membros materializados com `status='pending'`. */
    added: z.number().int().min(0),
    /** Já eram membros ativos de OUTRA campanha — materializados aqui também, com `status='pending'`. */
    reused: z.number().int().min(0),
    /** Materializados com `status='blocked'` — preferência de contato bloqueada (`assertContactAllowed`). */
    blocked: z.number().int().min(0),
    /** NÃO materializados — lead não encontrado no escopo ou sem telefone utilizável. */
    rejected: z.number().int().min(0),
    /** Já eram membros DESTA campanha — não reinseridos (idempotência). */
    alreadyPresent: z.number().int().min(0),
    /** `added + reused + blocked` — total efetivamente inserido nesta chamada. */
    materializedCount: z.number().int().min(0),
});
// ---------------------------------------------------------------------------
// PATCH /:id/members/:memberId — remove um membro da carteira (fora de
// hygiene, decisão do gestor). Exige motivo — auditado, nunca em texto pleno
// (LGPD §8.5, mesmo padrão de `CampaignQueueReleaseBodySchema`).
// ---------------------------------------------------------------------------
export const CampaignMemberRemoveBodySchema = z.object({
    reason: z
        .string()
        .min(1)
        .max(500)
        .describe('Motivo da remoção — obrigatório. Auditado apenas como indicador de presença (LGPD §8.5).'),
});
export const CampaignMemberRemoveResponseSchema = z.object({
    member: CampaignMemberSchema,
});
//# sourceMappingURL=sales-campaigns.js.map